import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Handle queryKey array: first element is the URL, rest are params
    const [url, ...params] = queryKey;
    let finalUrl = url as string;
    
    // If params are provided, handle them appropriately
    if (params.length > 0) {
      const validParams = params.filter(p => p !== null && p !== undefined && p !== '');
      if (validParams.length > 0) {
        // Only /api/sessions takes a query-param (?year=). Everything else
        // under /api/* is path-segment style — including /api/drivers/:k,
        // /api/weather/:k, /api/laps/:k/:driver, etc. The previous broader
        // check also routed /api/drivers as query-param, which produced
        // /api/drivers?year=11270 — a URL no server route matches, so the
        // SPA fallback returned HTML and JSON.parse threw silently. The
        // dashboard's drivers query had been quietly broken: it was only
        // working when the F1-livetiming-sourced fallback populated.
        const isQueryParamRoute = /\/api\/sessions(\?|$|\/)/.test(finalUrl);
        if (isQueryParamRoute) {
          const separator = finalUrl.includes('?') ? '&' : '?';
          if (validParams.length === 1 && !isNaN(Number(validParams[0]))) {
            finalUrl = `${finalUrl}${separator}year=${validParams[0]}`;
          } else {
            finalUrl = `${finalUrl}${separator}${validParams.map((p, i) => `param${i}=${encodeURIComponent(String(p))}`).join('&')}`;
          }
        } else {
          // Path segment style: /api/drivers/11270, /api/laps/11270/44, etc.
          finalUrl = `${finalUrl}/${validParams.map(p => encodeURIComponent(String(p))).join('/')}`;
        }
      }
    }
    
    const res = await fetch(finalUrl, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      // We previously had refetchOnWindowFocus: false everywhere, which makes
      // sense for completed sessions but bad for live ones — coming back to
      // the tab after a few minutes shouldn't show stale live data.
      // Per-query overrides handle this; the default stays off so historical
      // pages don't fire a refetch storm when the user tab-switches.
      refetchOnWindowFocus: false,
      // 5-minute default. Historical FastF1 endpoints set explicit 30-minute
      // staleTime, live queries set 0. This default kicks in for misc
      // endpoints (sessions list, drivers list, etc.) so the data refreshes
      // on the next page mount instead of being held forever.
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// historicalQueryOptions: completed-session data never changes once
// FastF1 has it. 30-minute stale window matches our CDN max-age, no
// retries needed.
export const historicalQueryOptions = {
  refetchInterval: false,
  refetchOnWindowFocus: false,
  staleTime: 30 * 60 * 1000,
  retry: false,
} as const;

// Returns the refetchInterval to use for a query whose data the WebSocket
// stream also pushes. Disables polling entirely when WS is connected — the
// stream is fresher and the HTTP poll would just burn bandwidth and battery.
//
// `isLive` makes the polling fall off for completed/historical sessions; the
// HTTP fallback is only useful while the session is actually running.
export function hasFreshLiveStream(stream: { wsConnected: boolean; connected: boolean; lastUpdate: number }, now = Date.now()): boolean {
  return stream.wsConnected && stream.connected && stream.lastUpdate > 0 && now - stream.lastUpdate < 15000;
}

export function livePollingInterval(
  isLive: boolean,
  wsConnected: boolean,
  fallbackMs: number,
): number | false {
  if (!isLive) return false;
  if (wsConnected) return false;
  return fallbackMs;
}
